(ns practitest.frontend.components.usage-and-limits
  (:require
    [practitest.frontend.common.fetch :refer [fetch-json]]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.components2.spinners :as spinners]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

;; ---- effects & events ---------

(re-frame/reg-fx
  ::load-fx
  (fn [{:keys [url on-success on-failure]}]
    (-> (fetch-json url {:method "GET"})
        (.then (fn [{:keys [ok? body]}]
                 (if ok?
                   (re-frame/dispatch (conj on-success body))
                   (re-frame/dispatch (conj on-failure body)))))
        (.catch (fn [err]
                  (re-frame/dispatch (conj on-failure {:error (.-message err)})))))))

(re-frame/reg-event-fx
  ::load!
  (fn [{:keys [db]} _]
    (let [account-id (subs/get-current-account-id db)]
      {:db (-> db
               (assoc-in [:usage-and-limits :loading?] true)
               (assoc-in [:usage-and-limits :error] nil))
       ::load-fx {:url (broutes/get-url "account/accounts#llm_budget_data"
                                        {:account_id account-id})
                  :on-success [::loaded]
                  :on-failure [::load-failed]}})))

(re-frame/reg-event-db
  ::loaded
  (fn [db [_ data]]
    (-> db
        (assoc-in [:usage-and-limits :loading?] false)
        (assoc-in [:usage-and-limits :data] data))))

(re-frame/reg-event-db
  ::load-failed
  (fn [db _]
    (-> db
        (assoc-in [:usage-and-limits :loading?] false)
        (assoc-in [:usage-and-limits :error]
                  (get-translation "usage-and-limits.error.load-failed"
                                   "Failed to load usage data")))))

;; ---- subs --------

(re-frame/reg-sub
  ::state
  (fn [db _]
    (:usage-and-limits db)))

(re-frame/reg-sub
  ::data
  :<- [::state]
  (fn [state _]
    (:data state)))

(re-frame/reg-sub
  ::loading?
  :<- [::state]
  (fn [state _]
    (:loading? state)))

(re-frame/reg-sub
  ::error
  :<- [::state]
  (fn [state _]
    (:error state)))

;; ---- helpers --------

(defn- pct [used budget]
  (if (pos? budget) (int (* 100 (/ used budget))) 0))

;; ---- views --------

(defn- status-badge [status]
  (let [[tk default cls] (case status
                           "ok" ["usage-and-limits.status.ok" "OK" "status-ok"]
                           "soft_limit" ["usage-and-limits.status.soft-limit" "Approaching limit" "status-warning"]
                           "hard_limit" ["usage-and-limits.status.hard-limit" "Over limit" "status-error"]
                           [nil status "status-unknown"])]
    [:span.usage-limits-status-badge {:class cls}
     (if tk (get-translation tk default) default)]))

(defn- summary-card [{:keys [used_cents budget_cents status month]}]
  (let [p (pct used_cents budget_cents)]
    [:div.usage-limits-card
     [:div.usage-limits-card-header
      [:h2 (get-translation "usage-and-limits.llm-usage.title" "LLM usage")]
      [:span.usage-limits-month month]]
     [:div.usage-limits-summary-row
      [:span.usage-limits-used (str p "%")]
      [:span.usage-limits-separator " " (get-translation "usage-and-limits.used" "used")]
      [status-badge status]]
     [:div.usage-limits-progress
      [:div.usage-limits-progress-fill
       {:class (str "fill-" status)
        :style {:width (str (min p 100) "%")}}]]]))

(defn- by-user-table [by-user budget-cents]
  (if (empty? by-user)
    [:div.usage-limits-empty (get-translation "usage-and-limits.by-user.empty" "No usage this month")]
    [:table.usage-limits-by-user
     [:thead
      [:tr
       [:th (get-translation "usage-and-limits.by-user.user-col" "User")]
       [:th (get-translation "usage-and-limits.by-user.feature-col" "Feature")]
       [:th.right (get-translation "usage-and-limits.by-user.uses-col" "Uses")]
       [:th.right (get-translation "usage-and-limits.by-user.usage-col" "Usage")]]]
     [:tbody
      (for [{:keys [user_id user_name feature uses cost_cents]} by-user]
        ^{:key (str user_id "-" feature)}
        [:tr
         [:td (or user_name (get-translation "usage-and-limits.by-user.deleted-user" "[deleted user]"))]
         [:td feature]
         [:td.right (or uses "-")]
         [:td.right (str (pct cost_cents budget-cents) "%")]])]
     [:tfoot
      [:tr.usage-limits-total-row
       [:td (get-translation "usage-and-limits.by-user.total" "Total")]
       [:td]
       [:td]
       [:td.right (str (pct (reduce + 0 (map :cost_cents by-user)) budget-cents) "%")]]]]))

(defn usage-and-limits-view []
  (let [loading? @(re-frame/subscribe [::loading?])
        error @(re-frame/subscribe [::error])
        data @(re-frame/subscribe [::data])]
    [:div.usage-and-limits-page
     [:h1 (get-translation "usage-and-limits.title" "Usage & Limits")]
     (cond
       loading? [:div.centered-spinner [spinners/mux-spinner {:size :modal}]]
       error [:div.usage-limits-error error]
       data [:<>
             [summary-card data]
             [:h3 (get-translation "usage-and-limits.by-user.title" "Usage by user")]
             [by-user-table (:by_user data) (:budget_cents data)]])]))

;; ---- route controller --------

(def main-controller
  {:start (fn [_params]
            (re-frame/dispatch [::load!]))})
